import { useState } from "react";

// BrandStrip — primary filter chip-row by retail brand (marca).
// Reads from the facility-dashboard payload's per-asset brand info,
// dedupes, and emits the selected brand id (or "all") via onChange.
// Hidden when brand-count <= 1 to avoid a redundant filter row.

type Brand = {
  id: string;
  name: string;
  count: number;
  // brands.config.logoUrl via the facility-dashboard payload. Null until
  // the org uploads one; the chip falls back to a monogram.
  logoUrl?: string | null;
};

type BrandStripProps = {
  brands: Brand[];
  value: string; // brand id, or "all"
  onChange: (id: string) => void;
  countLabel?: string; // "tiendas" | "abiertas" | …
  totalCount?: number;
};

function brandInitials(name: string): string {
  const cleaned = name.replace(/[^A-Za-zÀ-ÿ ]/g, "").trim();
  const words = cleaned.split(/\s+/).filter(Boolean);
  if (words.length === 0) return "??";
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

// Hash brand-name → consistent pastel bg for the chip mark. Keeps
// colors stable across renders without requiring a per-brand token.
function brandTone(name: string): { bg: string; fg: string } {
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = (h * 31 + name.charCodeAt(i)) >>> 0;
  }
  const hue = h % 360;
  return {
    bg: `hsl(${hue}deg 65% 92%)`,
    fg: `hsl(${hue}deg 55% 32%)`,
  };
}

export function BrandStrip({
  brands,
  value,
  onChange,
  countLabel = "tiendas",
  totalCount,
}: BrandStripProps) {
  // Brands whose logo 404'd or failed to decode — they degrade to the
  // monogram for the rest of the session. Declared before the early
  // return so the hook count stays stable as brands load in.
  const [brokenLogos, setBrokenLogos] = useState<ReadonlySet<string>>(() => new Set());

  if (brands.length <= 1) return null;

  const total = totalCount ?? brands.reduce((sum, b) => sum + b.count, 0);

  return (
    <div className="brand-strip" role="group" aria-label="Filtro por marca">
      <span className="brand-strip__label">Marca</span>

      <button
        type="button"
        className={`brand-chip brand-chip--all${value === "all" ? " brand-chip--active" : ""}`}
        onClick={() => onChange("all")}
        aria-pressed={value === "all"}
        aria-label={`Todas las marcas, ${total} ${countLabel}`}
      >
        <span className="brand-chip__mark" aria-hidden="true" />
        <span className="brand-chip__meta">
          <span className="brand-chip__name">Todas</span>
          <span className="brand-chip__sub">
            {total} {countLabel}
          </span>
        </span>
      </button>

      {brands.map((b) => {
        const tone = brandTone(b.name);
        const active = value === b.id;
        const showLogo = Boolean(b.logoUrl) && !brokenLogos.has(b.id);
        return (
          <button
            key={b.id}
            type="button"
            className={`brand-chip${active ? " brand-chip--active" : ""}`}
            onClick={() => onChange(b.id)}
            aria-pressed={active}
            aria-label={`${b.name}, ${b.count} ${countLabel}`}
          >
            {showLogo ? (
              <span className="brand-chip__mark brand-chip__mark--logo" aria-hidden="true">
                <img
                  src={b.logoUrl!}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  onError={() => setBrokenLogos((prev) => new Set(prev).add(b.id))}
                />
              </span>
            ) : (
              <span
                className="brand-chip__mark"
                style={{ background: tone.bg, color: tone.fg }}
                aria-hidden="true"
              >
                {brandInitials(b.name)}
              </span>
            )}
            <span className="brand-chip__meta">
              <span className="brand-chip__name">{b.name}</span>
              <span className="brand-chip__sub">
                {b.count} {countLabel}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
